
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  Search,
  X,
  ChevronLeft,
  ChevronRight,
  ArrowDownWideNarrow,
  Rss,
  CalendarDays,
  Tag,
  MapPin,
  Phone,
  Mail,
  Send,
  CheckCircle2,
  SlidersHorizontal,
} from "lucide-react";
import {
  Icon,
  Reveal,
  Eyebrow,
  SectionHeading,
  FieldPattern,
  Breadcrumb,
  SERIF,
} from "./ui";
import CouncilLeadership from "./CouncilLeadership.jsx";
import {
  SECTION_NAV,
  DEFAULT_IMAGES,
  getSector,
  splitHeroTitle,
} from "./SectorsData";
import { getSectorContact } from "./SectorHOD.jsx";
import QueryForm from "./QueryForm.jsx";

/* ============================ date helpers ============================ */

function getDateParts(dateStr = "") {
  const parts = dateStr.split(" ");
  const dayPart = parts[0] || "--";
  const restPart = parts.slice(1).join(" ");
  const monthPart = (restPart.split(",")[0] || "").split("-")[0].trim();
  const yearMatch = dateStr.match(/(20\d{2})/);
  const year = yearMatch ? yearMatch[1] : "2026";
  return { dayPart, monthPart, year };
}

function toDateObj(dateStr = "") {
  const { dayPart, monthPart, year } = getDateParts(dateStr);
  const cleanDay = dayPart.replace(/[^0-9]/g, "") || "1";
  const d = new Date(`${monthPart} ${cleanDay}, ${year}`);
  return isNaN(d.getTime()) ? null : d;
}

function isEventUpcoming(dateStr) {
  const d = toDateObj(dateStr);
  if (!d) return true;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return d.getTime() >= today.getTime();
}

function toTimestamp(dateStr = "") {
  const d = toDateObj(dateStr);
  return d ? d.getTime() : 0;
}

/* ========================================================================
   SectorsPage — ONE component, driven entirely by the :councilSlug route
   param. Every council reuses this exact layout; only the data changes.
   ======================================================================== */

export default function SectorPage() {
  // const { councilSlug } = useParams();
  // const sector = getSector(councilSlug);


  const { councilSlug } = useParams();
  const sector = getSector(councilSlug);
  const sectorContact = getSectorContact(councilSlug);

  const [progress, setProgress] = useState(0);
  const [active, setActive] = useState("overview");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  /* Desktop sidebar auto-hide (3s idle → slide away; mouse / left edge → show) */
  const [navVisible, setNavVisible] = useState(true);
  const [navPinned, setNavPinned] = useState(false); // hover on the menu itself
  const hideTimerRef = useRef(null);
  const navVisibleRef = useRef(true);
  const navPinnedRef = useRef(false);
  const clickLockRef = useRef(false); // don't fight spy right after a nav click
  const NAV_IDLE_MS = 3000;
  const LEFT_EDGE_PX = 28; // hover near left edge to reveal when hidden

  /* ------------------------- Press (Newsroom-style) ------------------------- */
  const [pressTab, setPressTab] = useState("releases"); // "releases" | "coverage"
  const [pressKeyword, setPressKeyword] = useState("");
  const [pressSort, setPressSort] = useState("newest"); // "newest" | "oldest"
  const [pressPage, setPressPage] = useState(1);
  const pressResultsRef = useRef(null);
  const PRESS_PAGE_SIZE = 5;

  /* -------------------------- Events (EventPage-style) -------------------------- */
  const [eventTab, setEventTab] = useState("all"); // "all" | "forthcoming" | "past"
  const [eventKeyword, setEventKeyword] = useState("");
  const [eventTag, setEventTag] = useState("");
  const [eventPlace, setEventPlace] = useState("");
  const [eventAnimKey, setEventAnimKey] = useState(0);
  const [eventsVisible, setEventsVisible] = useState(2);
  const EVENTS_STEP = 2;

  /* -------------------------- Secretariat contact form -------------------------- */
  const [submitted, setSubmitted] = useState(false);

  // Keep refs in sync so event handlers always see latest values
  useEffect(() => {
    navVisibleRef.current = navVisible;
  }, [navVisible]);
  useEffect(() => {
    navPinnedRef.current = navPinned;
  }, [navPinned]);

  /* ---- Show sidebar + (re)start 3s idle hide timer ---- */
  const bumpNavVisibility = () => {
    setNavVisible(true);
    navVisibleRef.current = true;
    if (hideTimerRef.current) window.clearTimeout(hideTimerRef.current);
    // Don't auto-hide while user is hovering the menu itself
    if (navPinnedRef.current) return;
    hideTimerRef.current = window.setTimeout(() => {
      // Still pinned? keep open
      if (navPinnedRef.current) return;
      setNavVisible(false);
      navVisibleRef.current = false;
    }, NAV_IDLE_MS);
  };

  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(h > 0 ? (window.scrollY / h) * 100 : 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* ---- Desktop: auto-hide sidebar after 3s of no mouse activity ---- */
  useEffect(() => {
    const onMove = (e) => {
      // Near left edge always reveals the menu
      if (e.clientX <= LEFT_EDGE_PX) {
        bumpNavVisibility();
        return;
      }
      // While menu is open (or pinned), any mouse move resets the 3s timer
      if (navVisibleRef.current || navPinnedRef.current) {
        bumpNavVisibility();
      }
    };

    const onActivity = () => {
      if (navVisibleRef.current || navPinnedRef.current) {
        bumpNavVisibility();
      }
    };

    // Start first hide countdown on mount
    bumpNavVisibility();

    window.addEventListener("mousemove", onMove, { passive: true });
    window.addEventListener("keydown", onActivity, { passive: true });
    // Note: we intentionally do NOT reset on wheel/scroll — idle = no mouse use

    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("keydown", onActivity);
      if (hideTimerRef.current) window.clearTimeout(hideTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---- Accurate current-section spy (scroll position based) ---- */
  useEffect(() => {
    const sectionIds = SECTION_NAV.map((s) => s.id);

    const pickActive = () => {
      // While a programmatic scroll-to-section is in flight, keep the clicked id
      if (clickLockRef.current) return;

      const marker = window.innerHeight * 0.28; // "reading line" near upper third
      let current = sectionIds[0];

      for (const id of sectionIds) {
        const el = document.getElementById(id);
        if (!el) continue;
        const top = el.getBoundingClientRect().top;
        // Section whose top has crossed (or is just above) the marker is active
        if (top - marker <= 0) {
          current = id;
        } else {
          break;
        }
      }

      // Near bottom of page → force last section active
      const doc = document.documentElement;
      if (window.scrollY + window.innerHeight >= doc.scrollHeight - 8) {
        current = sectionIds[sectionIds.length - 1];
      }

      setActive((prev) => (prev === current ? prev : current));
    };

    pickActive();
    window.addEventListener("scroll", pickActive, { passive: true });
    window.addEventListener("resize", pickActive, { passive: true });
    return () => {
      window.removeEventListener("scroll", pickActive);
      window.removeEventListener("resize", pickActive);
    };
  }, [sector]);

  // Reset all page-local UI state whenever the council changes (nav between sectors)
  useEffect(() => {
    setPressTab("releases");
    setPressKeyword("");
    setPressSort("newest");
    setPressPage(1);
    setEventTab("all");
    setEventKeyword("");
    setEventTag("");
    setEventPlace("");
    setEventsVisible(2);
    setMobileNavOpen(false);
    setNavVisible(true);
    setNavPinned(false);
    clickLockRef.current = false;
    window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" });
  }, [councilSlug]);

  // Handle hash on load / hash change so #section lands correctly
  useEffect(() => {
    const scrollToHash = () => {
      const hash = window.location.hash.replace("#", "");
      if (!hash) return;
      // Wait a tick so layout (images/fonts) can settle
      requestAnimationFrame(() => {
        const el = document.getElementById(hash);
        if (!el) return;
        const y = el.getBoundingClientRect().top + window.scrollY - 16;
        window.scrollTo({ top: y, behavior: "smooth" });
        setActive(hash);
      });
    };
    scrollToHash();
    window.addEventListener("hashchange", scrollToHash);
    return () => window.removeEventListener("hashchange", scrollToHash);
  }, [sector]);

  const scrollToSection = (id) => (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;

    // Lock active to the clicked section until smooth scroll settles
    clickLockRef.current = true;
    setActive(id);
    setMobileNavOpen(false);
    bumpNavVisibility();

    const y = el.getBoundingClientRect().top + window.scrollY - 16;
    window.scrollTo({ top: y, behavior: "smooth" });

    // Release lock after scroll animation (~700ms) + small buffer
    window.setTimeout(() => {
      clickLockRef.current = false;
      // One more pick so we land on the true section if offset drifted
      const marker = window.innerHeight * 0.28;
      let current = id;
      for (const s of SECTION_NAV) {
        const node = document.getElementById(s.id);
        if (!node) continue;
        if (node.getBoundingClientRect().top - marker <= 0) current = s.id;
        else break;
      }
      setActive(current);
    }, 900);

    if (window.history?.replaceState) {
      window.history.replaceState(null, "", `#${id}`);
    } else {
      window.location.hash = id;
    }
  };

  /* ---------------------------- Data sourced from the council ---------------------------- */
  const PUBLICATIONS = sector?.publications || [];
  const PRESS_RELEASES = sector?.pressReleases || [];
  const PRESS_COVERAGE = sector?.pressCoverage || [];
  const EVENTS = sector?.events || [];

  /* ---------------------------- Press derived state ---------------------------- */
  const pressSource = pressTab === "releases" ? PRESS_RELEASES : PRESS_COVERAGE;

  const pressFiltered = useMemo(() => {
    const kw = pressKeyword.trim().toLowerCase();
    let list = pressSource.filter((item) => {
      if (!kw) return true;
      const hay = `${item.title} ${item.excerpt || ""}`.toLowerCase();
      return hay.includes(kw);
    });
    list = [...list].sort((a, b) => {
      const ta = toTimestamp(a.date);
      const tb = toTimestamp(b.date);
      return pressSort === "newest" ? tb - ta : ta - tb;
    });
    return list;
  }, [pressSource, pressKeyword, pressSort]);

  useEffect(() => setPressPage(1), [pressTab, pressKeyword, pressSort]);

  const pressTotalPages = Math.max(1, Math.ceil(pressFiltered.length / PRESS_PAGE_SIZE));
  const pressClampedPage = Math.min(pressPage, pressTotalPages);
  const pressStart = (pressClampedPage - 1) * PRESS_PAGE_SIZE;
  const pressPageItems = pressFiltered.slice(pressStart, pressStart + PRESS_PAGE_SIZE);

  const scrollToPressResults = () =>
    pressResultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const changePressPage = (n) => {
    setPressPage(n);
    scrollToPressResults();
  };

  /* ---------------------------- Events derived state ---------------------------- */
  const eventTags = useMemo(
    () => Array.from(new Set(EVENTS.map((e) => e.tag).filter(Boolean))),
    [EVENTS]
  );

  const eventPlaces = useMemo(
    () => Array.from(new Set(EVENTS.map((e) => e.place).filter(Boolean))),
    [EVENTS]
  );

  const filteredEvents = useMemo(() => {
    const kw = eventKeyword.trim().toLowerCase();
    return EVENTS.filter((e) => {
      const upcoming = isEventUpcoming(e.date);
      if (eventTab === "forthcoming" && !upcoming) return false;
      if (eventTab === "past" && upcoming) return false;
      if (eventTag && e.tag !== eventTag) return false;
      if (eventPlace && e.place !== eventPlace) return false;
      if (kw) {
        const hay = `${e.title} ${e.text} ${e.tag || ""} ${e.place || ""}`.toLowerCase();
        if (!hay.includes(kw)) return false;
      }
      return true;
    });
  }, [eventTab, eventTag, eventPlace, eventKeyword, EVENTS]);

  useEffect(() => {
    setEventAnimKey((k) => k + 1);
  }, [eventTab, eventTag, eventPlace]);

  useEffect(() => {
    setEventsVisible(2);
  }, [eventTab, eventTag, eventPlace, eventKeyword]);

  const clearEventFilters = () => {
    setEventKeyword("");
    setEventTag("");
    setEventPlace("");
    setEventTab("all");
    setEventAnimKey((k) => k + 1);
  };

  const visibleEvents = filteredEvents.slice(0, eventsVisible);
  const hasMoreEvents = eventsVisible < filteredEvents.length;
  const loadMoreEvents = () =>
    setEventsVisible((v) => Math.min(v + EVENTS_STEP, filteredEvents.length));

  /* ---------------------------- Secretariat form handler ---------------------------- */
  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    window.setTimeout(() => setSubmitted(false), 5000);
  };

  /* ---------------------------- Council not found ---------------------------- */
  if (!sector) {
    return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center gap-4 bg-[#FAF7F2] px-6 text-center">
        <p className="text-[0.75rem] font-bold uppercase tracking-[0.22em] text-[#DB6400]">
          Council not found
        </p>
        <h1 style={SERIF} className="text-2xl font-bold text-stone-900 sm:text-3xl">
          We couldn't find that council page
        </h1>
        <p className="max-w-md text-[14px] text-stone-500">
          The council you're looking for may have moved, or the link may be out of date.
        </p>
        <Link
          to="/"
          className="mt-2 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#DA2128] to-[#DB6400] px-6 py-2.5 text-[12.5px] font-bold text-white shadow-lg shadow-[#DA2128]/25"
        >
          Back to Home
        </Link>
      </div>
    );
  }

  const heroImage = sector.heroImage || DEFAULT_IMAGES.hero;
  const overviewImage = sector.overviewImage || DEFAULT_IMAGES.overview;
  const accentImage = sector.accentImage || DEFAULT_IMAGES.accent;
  const { line1: heroLine1, line2: heroLine2 } = splitHeroTitle(sector.title);
  const overviewParagraphs = sector.overview || [];
  const leadership = sector.leadership || [];

  /* Shared nav items renderer (desktop sidebar + mobile drawer) */
  const NavItems = ({ onItemClick, compact = false }) => (
    <ul className={`flex flex-col ${compact ? "gap-0.5" : "gap-1"}`}>
      {SECTION_NAV.map((item) => {
        const isActive = active === item.id;
        return (
          <li key={item.id}>
            <button
              type="button"
              onClick={(e) => {
                scrollToSection(item.id)(e);
                onItemClick?.(item.id);
              }}
              className={`group relative flex w-full items-center gap-2.5 rounded-xl text-left font-bold uppercase tracking-[0.08em] transition-all duration-300 ${
                compact ? "px-2.5 py-2.5 text-[11px]" : "px-3.5 py-3 text-[12.5px]"
              } ${
                isActive
                  ? "bg-gradient-to-r from-[#DA2128] to-[#DB6400] text-white shadow-md shadow-[#DA2128]/25"
                  : "text-stone-500 hover:bg-[#FEF7F4] hover:text-stone-800"
              }`}
            >
              {/* Active left accent bar */}
              <span
                className={`absolute left-0 top-1/2 h-5 w-1 -translate-y-1/2 rounded-r-full transition-all duration-300 ${
                  isActive ? "bg-white/90" : "bg-transparent group-hover:bg-[#DA2128]/40"
                }`}
              />
              <span
                className={`grid shrink-0 place-items-center rounded-lg font-black transition-colors ${
                  compact ? "h-6 w-6 text-[10px]" : "h-7 w-7 text-[11px]"
                } ${
                  isActive
                    ? "bg-white/20 text-white"
                    : "bg-stone-100 text-stone-400 group-hover:bg-[#DA2128]/10 group-hover:text-[#DA2128]"
                }`}
              >
                {item.label.charAt(0)}
              </span>
              <span className="truncate leading-tight">{item.label}</span>
            </button>
          </li>
        );
      })}
    </ul>
  );

  return (
    <div className="min-h-screen bg-[#FAF7F2] antialiased">
      <style>{`
        html { scroll-behavior: smooth; }
        body { background:#FAF7F2; color:#1c1917; font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif; -webkit-font-smoothing: antialiased; }
        ::selection { background:#DA2128; color:#fff; }
        ::-webkit-scrollbar { width:10px; height:10px; }
        ::-webkit-scrollbar-track { background:#FEF7F4; }
        ::-webkit-scrollbar-thumb { background:linear-gradient(#DA2128,#DB6400); border-radius:9999px; }
        @keyframes agri-float { 0%,100%{transform:translateY(0) rotate(0)} 50%{transform:translateY(-16px) rotate(3deg)} }
        @keyframes agri-float-slow { 0%,100%{transform:translate(0,0)} 50%{transform:translate(10px,-22px)} }
        @keyframes agri-sway { 0%,100%{transform:rotate(-6deg)} 50%{transform:rotate(6deg)} }
        @keyframes agri-shine { 0%{transform:translateX(-120%) skewX(-20deg)} 60%,100%{transform:translateX(240%) skewX(-20deg)} }
        @keyframes agri-pulse-ring { 0%{transform:scale(.85);opacity:.7} 70%{transform:scale(1.5);opacity:0} 100%{opacity:0} }
        @keyframes agri-blob { 0%,100%{border-radius:62% 38% 46% 54% / 55% 48% 52% 45%} 50%{border-radius:42% 58% 66% 34% / 38% 62% 38% 62%} }
        .anim-float{animation:agri-float 7s ease-in-out infinite}
        .anim-float-slow{animation:agri-float-slow 11s ease-in-out infinite}
        .anim-sway{animation:agri-sway 5s ease-in-out infinite;transform-origin:bottom center}
        .anim-blob{animation:agri-blob 14s ease-in-out infinite}
        .anim-pulse-ring{animation:agri-pulse-ring 2.6s cubic-bezier(.22,.61,.36,1) infinite}
        .shine-parent{position:relative;overflow:hidden}
        .shine-parent::after{content:"";position:absolute;top:0;bottom:0;left:0;width:45%;background:linear-gradient(90deg,transparent,rgba(255,255,255,.45),transparent);transform:translateX(-120%) skewX(-20deg)}
        .shine-parent:hover::after{animation:agri-shine 1s ease-out}
        .reveal{opacity:0;transform:translateY(26px);transition:opacity .7s cubic-bezier(.22,1,.36,1),transform .7s cubic-bezier(.22,1,.36,1)}
        .reveal-left{opacity:0;transform:translateX(-32px);transition:opacity .8s cubic-bezier(.22,1,.36,1),transform .8s cubic-bezier(.22,1,.36,1)}
        .reveal-right{opacity:0;transform:translateX(32px);transition:opacity .8s cubic-bezier(.22,1,.36,1),transform .8s cubic-bezier(.22,1,.36,1)}
        .reveal-zoom{opacity:0;transform:scale(.94);transition:opacity .7s cubic-bezier(.22,1,.36,1),transform .7s cubic-bezier(.22,1,.36,1)}
        .is-visible{opacity:1!important;transform:none!important}
        .event-fade-up { animation: fade-up 0.6s cubic-bezier(.2,.8,.2,1) both; }
        @keyframes fade-up { 0% { opacity: 0; transform: translateY(22px); } 100% { opacity: 1; transform: translateY(0); } }
        .event-card-shine::before { content: ""; position: absolute; inset: 0; background: linear-gradient(120deg, transparent 30%, rgba(255,255,255,.7) 50%, transparent 70%); transform: translateX(-140%) skewX(-18deg); opacity: 0; transition: opacity .3s ease; }
        .group:hover .event-card-shine::before { animation: shimmer 1s ease forwards; opacity: 1; }
        @keyframes shimmer { 0% { transform: translateX(-140%) skewX(-18deg); } 100% { transform: translateX(220%) skewX(-18deg); } }
        .event-glow-border { border: 1.5px solid transparent; background: linear-gradient(#fff, #fff) padding-box, linear-gradient(135deg, rgba(218,33,40,0.55), rgba(245,156,0,0.55)) border-box; }
        .event-live-pill { animation: pulse-glow 2.4s infinite; }
        @keyframes pulse-glow { 0%, 100% { box-shadow: 0 0 0 0 rgba(245,156,0,0.3); } 50% { box-shadow: 0 0 0 10px rgba(245,156,0,0); } }
        .font-heading { font-family: 'Fraunces', Georgia, serif; }
        /* Section scroll offset — accounts for sticky bits / comfortable landing */
        .sector-section { scroll-margin-top: 1.25rem; }
        .write-vertical {
          writing-mode: vertical-rl;
          text-orientation: mixed;
          transform: rotate(180deg);
          letter-spacing: 0.14em;
        }
        @media (prefers-reduced-motion: reduce){*,*::before,*::after{animation-duration:.001ms!important;animation-iteration-count:1!important;transition-duration:.001ms!important}}
      `}</style>

      {/* Scroll progress */}
      <div className="fixed inset-x-0 top-0 z-[60] h-1">
        <div
          className="h-full bg-gradient-to-r from-[#DA2128] via-[#DB6400] to-[#F59C00] transition-[width] duration-150"
          style={{ width: `${progress}%` }}
        />
      </div>

      <button
        type="button"
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        aria-label="Back to top"
        className={`fixed bottom-7 right-7 z-50 flex h-12 w-12 items-center justify-center rounded-full bg-[#DA2128] text-white shadow-xl shadow-[#DA2128]/35 transition-all duration-300 hover:-translate-y-1 hover:bg-[#DB6400] ${
          progress > 8 ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-6 opacity-0"
        }`}
      >
        <Icon name="chevron" className="h-5 w-5 rotate-180" strokeWidth={2.6} />
      </button>

      {/* Mobile / tablet nav toggle (desktop floating sidebar starts at xl) */}
      <button
        type="button"
        onClick={() => setMobileNavOpen(true)}
        aria-label="Open section menu"
        className={`fixed bottom-7 left-7 z-50 flex h-12 items-center gap-2 rounded-full bg-white px-4 text-[12px] font-bold uppercase tracking-[0.1em] text-[#DA2128] shadow-xl ring-1 ring-[#DA2128]/20 transition-all duration-300 hover:-translate-y-1 xl:hidden ${
          progress > 8 ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-6 opacity-0"
        }`}
      >
        <SlidersHorizontal className="h-4 w-4" />
        Menu
      </button>

      {/* Mobile drawer overlay */}
      <AnimatePresence>
        {mobileNavOpen && (
          <>
            <motion.div
              key="overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[70] bg-stone-900/40 backdrop-blur-sm xl:hidden"
              onClick={() => setMobileNavOpen(false)}
            />
            <motion.aside
              key="drawer"
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "spring", stiffness: 380, damping: 36 }}
              className="fixed inset-y-0 left-0 z-[80] flex w-[280px] max-w-[85vw] flex-col bg-white shadow-2xl xl:hidden"
            >
              <div className="flex items-center justify-between border-b border-stone-200 px-5 py-4">
                <div>
                  <p className="text-[0.65rem] font-bold uppercase tracking-[0.22em] text-[#DB6400]">
                    Navigate
                  </p>
                  <p style={SERIF} className="text-lg font-bold text-stone-900">
                    Sections
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setMobileNavOpen(false)}
                  className="grid h-9 w-9 place-items-center rounded-xl bg-stone-100 text-stone-600 transition hover:bg-[#FEF7F4] hover:text-[#DA2128]"
                  aria-label="Close menu"
                >
                  <X className="h-4 w-4" strokeWidth={2.4} />
                </button>
              </div>
              {/* <div className="flex-1 overflow-y-auto px-3 py-4">
                <NavItems />
              </div> */}
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      <main>
        {/* ==============================================================
            HERO — same two-column layout for every council: eyebrow +
            title + hero image, driven by the council's own data.
           ============================================================== */}
        <section id="home" className="relative overflow-hidden bg-gradient-to-b from-white via-white to-stone-50">
          <Breadcrumb light />

          {/* FieldPattern bas is section ke andar, container-restricted hai */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <FieldPattern className="text-black" opacity={0.06} />
          </div>

          <div className="anim-blob anim-float pointer-events-none absolute -right-28 top-10 h-80 w-80 bg-[#F59C00]/10 blur-3xl" />
          <div className="anim-blob anim-float-slow pointer-events-none absolute -left-24 bottom-10 h-72 w-72 bg-[#DA2128]/15 blur-3xl" />

          <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-6 pb-16 pt-20 lg:grid-cols-[1.08fr_1fr] lg:gap-10 lg:pb-24 lg:pt-24">
            {/* Left — copy */}
            <div>
              <Reveal delay={60}>
                <Eyebrow>{sector.sectorGroup}</Eyebrow>
              </Reveal>

              <Reveal delay={130}>
                <h1
                  style={SERIF}
                  className="mt-5 text-4xl font-bold leading-[1.08] tracking-tight text-black sm:text-5xl lg:text-[3.3rem]"
                >
                  {heroLine1}
                  {heroLine2 && (
                    <span className="mt-1 block bg-gradient-to-r from-[#F59C00] via-[#DB6400] to-[#DA2128] bg-clip-text text-transparent">
                      {heroLine2}
                    </span>
                  )}
                </h1>
              </Reveal>
            </div>

            {/* Right — single hero image */}
            <Reveal variant="right" delay={180}>
              <div className="relative mx-auto max-w-md lg:max-w-none">
                <div className="relative overflow-hidden rounded-[28px] border-4 border-stone-200 shadow-xl shadow-black/10">
                  <img
                    src={heroImage}
                    alt={sector.title}
                    className="h-[320px] w-full object-cover sm:h-[260px] lg:h-[300px]"
                  />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent" />
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        {/* ===================== LEFT FLOATING SIDEBAR (outside content width) =====================
            - Sits in the left gutter so content stays perfectly centered
            - Auto-hides after 3s idle; reappears on mouse near left edge / any activity while open
            - Hovering the menu pins it open
        */}
        {/* Invisible left-edge hotzone — always catches mouse to re-show the menu */}
        <div
          aria-hidden="true"
          className="fixed inset-y-0 left-0 z-40 hidden w-3 xl:block"
          onMouseEnter={() => {
            setNavVisible(true);
            bumpNavVisibility();
          }}
        />

        <aside
          aria-label="Section navigation"
          aria-hidden={!navVisible}
          onMouseEnter={() => {
            setNavPinned(true);
            setNavVisible(true);
            if (hideTimerRef.current) window.clearTimeout(hideTimerRef.current);
          }}
          onMouseLeave={() => {
            setNavPinned(false);
            bumpNavVisibility(); // restart 3s countdown after leaving
          }}
          className={`fixed left-0 top-1/2 z-40 hidden w-[200px] -translate-y-1/2 pl-3 transition-all duration-500 ease-[cubic-bezier(.22,1,.36,1)] xl:block 2xl:w-[220px] 2xl:pl-5 ${
            navVisible
              ? "pointer-events-auto translate-x-0 opacity-100"
              : "pointer-events-none -translate-x-[110%] opacity-0"
          }`}
        >
          
        </aside>

        {/* Slim peek tab when sidebar is hidden — click / hover to open */}
        <button
          type="button"
          aria-label="Show section menu"
          onMouseEnter={() => {
            setNavVisible(true);
            bumpNavVisibility();
          }}
          onClick={() => {
            setNavVisible(true);
            bumpNavVisibility();
          }}
          className={`fixed left-0 top-1/2 z-40 hidden -translate-y-1/2 flex-col items-center gap-1 rounded-r-xl border border-l-0 border-stone-200/80 bg-white/95 px-1.5 py-3 shadow-lg backdrop-blur-xl transition-all duration-500 ease-[cubic-bezier(.22,1,.36,1)] xl:flex ${
            navVisible
              ? "pointer-events-none -translate-x-full opacity-0"
              : "pointer-events-auto translate-x-0 opacity-100"
          }`}
        >
          <span className="h-8 w-1 rounded-full bg-gradient-to-b from-[#DA2128] to-[#F59C00]" />
          <span className="write-vertical text-[9px] font-extrabold uppercase tracking-[0.18em] text-[#DA2128]">
            {SECTION_NAV.find((s) => s.id === active)?.label?.slice(0, 3) || "NAV"}
          </span>
        </button>

        {/* ============================= OVERVIEW ============================= */}
        <section
          id="overview"
          className="sector-section relative scroll-mt-6 overflow-hidden bg-white py-20 lg:py-24"
        >
          <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-6 lg:grid-cols-2 lg:gap-16">
            <Reveal variant="left">
              <div className="relative">
                <div className="overflow-hidden rounded-[32px] border-4 border-white shadow-2xl shadow-stone-900/15">
                  <img
                    src={overviewImage}
                    alt={sector.title}
                    className="h-[400px] w-full object-cover transition-transform duration-[1400ms] hover:scale-105"
                    loading="lazy"
                  />
                </div>
                <div className="anim-float absolute -bottom-8 -right-4 w-56 rounded-2xl bg-white p-5 shadow-2xl">
                  <p className="mt-2 text-[14.5px] font-bold leading-snug text-stone-900">
                    {sector.title}
                  </p>
                </div>
                <div className="anim-float-slow absolute -left-6 -top-8 hidden w-36 overflow-hidden rounded-2xl border-4 border-white shadow-xl sm:block">
                  <img src={accentImage} alt="" className="h-24 w-full object-cover" loading="lazy" />
                </div>
              </div>
            </Reveal>

            <div>
              <SectionHeading
                align="left"
                eyebrow="Overview"
                title={
                  <>
                    Shaping India&apos;s{" "}
                    <span className="text-[#DA2128]">
                      {sector.sectorGroup.split(" ")[0].toLowerCase()} agenda
                    </span>
                  </>
                }
              />

              <div className="mt-6 space-y-4 border-l-2 border-[#DA2128]/20 pl-5">
                {overviewParagraphs.map((p, i) => (
                  <Reveal key={i} variant="right" delay={200 + i * 70}>
                    <p className="text-[15.5px] leading-relaxed text-stone-600">
                      {i === 0 && p.includes("ASSOCHAM") ? (
                        <>
                          <span className="font-bold text-stone-900">ASSOCHAM</span>
                          {p.replace("ASSOCHAM", "")}
                        </>
                      ) : (
                        p
                      )}
                    </p>
                  </Reveal>
                ))}
              </div>

              {sector.priorityAreas && sector.priorityAreas.length > 0 && (
                <div className="mt-8 grid gap-3 sm:grid-cols-2">
                  {sector.priorityAreas.map((area, i) => (
                    <div
                      key={i}
                      className="flex items-start gap-2.5 rounded-2xl bg-white/70 p-3.5 ring-1 ring-stone-200"
                    >
                      <Icon
                        name="check"
                        className="mt-0.5 h-4 w-4 shrink-0 text-[#DA2128]"
                        strokeWidth={2.8}
                      />
                      <p className="text-[13px] leading-relaxed text-stone-700">{area}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* =========================== PUBLICATIONS =========================== */}
        <section
          id="publications"
          className="sector-section relative scroll-mt-6 overflow-hidden bg-gradient-to-b from-[#FEF7F4] via-[#FAF7F2] to-[#FEF7F4] py-20 lg:py-24"
        >
          <FieldPattern className="text-[#DB6400]" opacity={0.05} />
          <div className="relative mx-auto max-w-7xl px-6">
            <SectionHeading eyebrow="Knowledge Hub" title={<>Publications &amp; Reports</>} />

            {PUBLICATIONS.length > 0 ? (
              <div className="mt-14 grid gap-7 lg:grid-cols-3">
                {PUBLICATIONS.map((p, i) => (
                  <Reveal key={p.title} delay={i * 110}>
                    <article className="group relative flex h-full flex-col overflow-hidden rounded-3xl border border-stone-200 bg-white transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl hover:shadow-[#DA2128]/10">
                      <div className="relative h-36 overflow-hidden bg-gradient-to-br from-[#DA2128] via-[#DB2628] to-[#DB6400]">
                        <svg
                          className="absolute inset-0 h-full w-full text-white/20"
                          viewBox="0 0 400 160"
                          preserveAspectRatio="none"
                          aria-hidden="true"
                        >
                          <path
                            d="M0 120 Q60 90 120 118 T240 116 T400 96 L400 160 L0 160Z"
                            fill="currentColor"
                          />
                          <path
                            d="M0 138 Q80 112 160 136 T320 130 T400 124 L400 160 L0 160Z"
                            fill="currentColor"
                            opacity="0.6"
                          />
                        </svg>
                        <Icon
                          name="wheat"
                          className="anim-sway absolute right-6 top-5 h-14 w-14 text-white/30"
                          strokeWidth={1.3}
                        />
                        <div className="absolute left-6 top-5 flex flex-col gap-2">
                          <span className="w-max rounded-full bg-white/20 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.14em] text-white backdrop-blur">
                            {p.date}
                          </span>
                          <span className="w-max rounded-full bg-white px-3 py-1 text-[11px] font-bold text-[#DA2128]">
                            {p.partner}
                          </span>
                        </div>
                      </div>
                      <div className="flex flex-1 flex-col p-7">
                        <h3
                          style={SERIF}
                          className="text-[19px] font-bold leading-snug text-stone-900 transition-colors group-hover:text-[#DA2128]"
                        >
                          {p.title}
                        </h3>
                        <p className="mt-3 flex-1 text-[14px] leading-relaxed text-stone-600">
                          {p.text}
                        </p>
                        <div className="mt-6 flex items-center justify-between gap-3">
                          <button
                            type="button"
                            className="inline-flex items-center gap-2 rounded-full border border-[#DA2128]/25 bg-[#FEF7F4] px-5 py-2.5 text-[12.5px] font-bold text-[#DA2128] transition-all duration-300 hover:bg-[#DA2128] hover:text-white"
                          >
                            Download PDF
                            <Icon name="download" className="h-4 w-4" strokeWidth={2.2} />
                          </button>
                          <span className="text-[11.5px] font-semibold uppercase tracking-wide text-stone-400">
                            {p.pages}
                          </span>
                        </div>
                      </div>
                    </article>
                  </Reveal>
                ))}
              </div>
            ) : (
              <div className="mt-14 flex flex-col items-center rounded-[24px] border border-dashed border-[#DA2128]/25 bg-white/60 py-16 text-center">
                <div className="grid h-16 w-16 place-items-center rounded-3xl bg-[#FEF7F4]">
                  <Icon name="wheat" className="h-7 w-7 text-[#DA2128]" strokeWidth={1.6} />
                </div>
                <h4 style={SERIF} className="mt-5 text-lg font-bold text-stone-900">
                  Publications coming soon
                </h4>
                <p className="mt-2 max-w-sm text-[13px] text-stone-500">
                  This council hasn't published a report yet. Check back soon, or explore other
                  sections above.
                </p>
              </div>
            )}
          </div>
        </section>

        {/* ============================= COUNCILS ============================= */}
        <section
          id="councils"
          className="sector-section relative scroll-mt-6 overflow-hidden bg-white py-20 lg:py-24"
        >
          <div className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-[#FAF7F2] to-transparent" />
          <div className="relative mx-auto max-w-7xl px-6">
            <SectionHeading eyebrow="Councils" title="Council Leadership" />
            <CouncilLeadership members={leadership} mandate={sector.priorityAreas} />
          </div>
        </section>

      

        {/* ======================= PRESS (Newsroom-listing style) ======================= */}
        <section
          id="press"
          className="sector-section relative scroll-mt-6 overflow-hidden bg-[#FAF7F2] py-20 lg:py-24"
        >
          <FieldPattern className="text-[#DA2128]" opacity={0.04} />
          <div className="relative mx-auto max-w-7xl px-6">
            <SectionHeading
              eyebrow="Newsroom"
              title={
                <>
                  Press Release &amp; <span className="text-[#DA2128]">Press Coverage</span>
                </>
              }
            />

            {/* Filter bar */}
            <Reveal delay={80}>
              <div className="mt-12 rounded-2xl border border-stone-200 bg-white p-4 shadow-[0_10px_40px_-24px_rgba(218,33,40,0.35)] sm:p-5">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                  {/* Type toggle */}
                  <div className="inline-flex w-fit rounded-full bg-stone-100 p-1">
                    {[
                      { key: "releases", label: "Press Releases", count: PRESS_RELEASES.length },
                      { key: "coverage", label: "Press Coverage", count: PRESS_COVERAGE.length },
                    ].map((t) => (
                      <button
                        key={t.key}
                        type="button"
                        onClick={() => setPressTab(t.key)}
                        className={`relative rounded-full px-5 py-2.5 text-[12.5px] font-bold uppercase tracking-[0.06em] transition-all duration-300 ${
                          pressTab === t.key
                            ? "bg-white text-[#DA2128] shadow-md"
                            : "text-stone-500 hover:text-stone-700"
                        }`}
                      >
                        {t.label}
                        <span
                          className={`ml-2 rounded-full px-2 py-0.5 text-[10px] ${
                            pressTab === t.key
                              ? "bg-[#DA2128]/10 text-[#DA2128]"
                              : "bg-stone-200 text-stone-500"
                          }`}
                        >
                          {t.count}
                        </span>
                      </button>
                    ))}
                  </div>

                  {/* Keyword + sort */}
                  <div className="flex flex-1 flex-col gap-3 sm:flex-row sm:items-center lg:max-w-xl">
                    <div className="relative flex-1">
                      <Search
                        className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#DA2128]"
                        strokeWidth={2.2}
                      />
                      <input
                        type="text"
                        value={pressKeyword}
                        onChange={(e) => setPressKeyword(e.target.value)}
                        placeholder="Search press releases & coverage..."
                        className="w-full rounded-xl border border-stone-200 bg-[#FAF7F2] py-2.5 pl-10 pr-3.5 text-[13px] font-medium text-stone-700 transition duration-300 placeholder:text-stone-400 hover:border-[#DA2128]/30 focus:border-[#DA2128] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#DA2128]/10"
                      />
                    </div>

                    <div className="flex shrink-0 items-center gap-2">
                      <ArrowDownWideNarrow className="h-4 w-4 text-stone-400" />
                      <div className="flex overflow-hidden rounded-xl text-[11.5px] font-bold ring-1 ring-stone-200">
                        {[
                          ["newest", "Newest"],
                          ["oldest", "Oldest"],
                        ].map(([v, l]) => (
                          <button
                            key={v}
                            type="button"
                            onClick={() => setPressSort(v)}
                            className={`px-3.5 py-2.5 transition-colors ${
                              pressSort === v
                                ? "bg-gradient-to-r from-[#DA2128] to-[#DB6400] text-white"
                                : "bg-white text-stone-500 hover:text-[#DA2128]"
                            }`}
                          >
                            {l}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Active filter chip */}
                <AnimatePresence>
                  {pressKeyword.trim() && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="flex flex-wrap items-center gap-2 pt-4">
                        <span className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-stone-400">
                          Active filter —
                        </span>
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-[#DA2128]/25 bg-[#FEF7F4] py-1 pl-3 pr-1.5 text-[11.5px] font-bold text-[#DA2128]">
                          "{pressKeyword.trim()}"
                          <button
                            type="button"
                            onClick={() => setPressKeyword("")}
                            className="grid h-[18px] w-[18px] place-items-center rounded-full bg-[#DA2128]/10 text-[#DA2128] transition-colors hover:bg-[#DA2128] hover:text-white"
                          >
                            <X className="h-2.5 w-2.5" strokeWidth={3} />
                          </button>
                        </span>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </Reveal>

            {/* Results */}
            <div ref={pressResultsRef} className="mt-10 scroll-mt-32">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b-2 border-stone-900/90 pb-4">
                <h3
                  style={SERIF}
                  className="flex items-center gap-2.5 text-xl font-bold text-stone-900 sm:text-2xl"
                >
                  {pressTab === "releases" ? "Press Releases" : "Press Coverage"}
                  <button
                    type="button"
                    title="Subscribe via RSS"
                    className="text-[#DA2128] transition-colors hover:text-[#DB6400]"
                    onClick={(e) => e.preventDefault()}
                  >
                    <Rss className="h-4 w-4" />
                  </button>
                </h3>
                <span className="text-[12.5px] font-semibold text-stone-400">
                  {pressFiltered.length === 0
                    ? "No matching results"
                    : `Showing ${pressStart + 1}–${Math.min(
                        pressStart + PRESS_PAGE_SIZE,
                        pressFiltered.length
                      )} of ${pressFiltered.length}`}
                </span>
              </div>

              {pressPageItems.length > 0 ? (
                <AnimatePresence mode="popLayout">
                  {pressPageItems.map((item, i) => (
                    <motion.article
                      key={`${pressTab}-${item.title}-${i}`}
                      layout
                      initial={{ opacity: 0, y: 18 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -12 }}
                      transition={{ duration: 0.35, delay: i * 0.05 }}
                      className="group flex flex-col gap-4 border-b border-stone-200 py-6 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2.5">
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#FEF7F4] px-2.5 py-1 text-[10.5px] font-extrabold uppercase tracking-[0.14em] text-[#DA2128]">
                            <CalendarDays className="h-3 w-3" />
                            {item.date}
                          </span>
                          <span className="inline-flex rounded-full border border-stone-200 px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-[0.1em] text-stone-500">
                            {pressTab === "releases" ? "Release" : "Coverage"}
                          </span>
                        </div>
                        <h4
                          style={SERIF}
                          className="mt-3 text-[17px] font-bold leading-snug text-stone-900 transition-colors group-hover:text-[#DA2128] sm:text-[18px]"
                        >
                          {item.title}
                        </h4>
                        <p className="mt-2 text-[13.5px] leading-relaxed text-stone-600">
                          {item.excerpt}
                        </p>
                      </div>
                      <a
                        href="#"
                        className="inline-flex shrink-0 items-center gap-1.5 self-start rounded-full border border-[#DA2128]/25 px-4 py-2 text-[12.5px] font-bold text-[#DA2128] transition-all duration-300 hover:bg-[#DA2128] hover:text-white sm:self-center"
                      >
                        Read more
                        <Icon name="chevron" className="h-3.5 w-3.5 rotate-90" strokeWidth={2.4} />
                      </a>
                    </motion.article>
                  ))}
                </AnimatePresence>
              ) : (
                <div className="flex flex-col items-center py-16 text-center">
                  <div className="grid h-16 w-16 place-items-center rounded-3xl bg-[#FEF7F4]">
                    <Search className="h-7 w-7 text-[#DA2128]" />
                  </div>
                  <h4 style={SERIF} className="mt-5 text-lg font-bold text-stone-900">
                    {pressSource.length === 0 ? "Nothing published yet" : "No results found"}
                  </h4>
                  <p className="mt-2 max-w-sm text-[13px] text-stone-500">
                    {pressSource.length === 0
                      ? "This council hasn't shared a press release or coverage item yet."
                      : "Nothing matches your search. Try a different keyword or clear it to see everything."}
                  </p>
                  {pressKeyword && (
                    <button
                      type="button"
                      onClick={() => setPressKeyword("")}
                      className="mt-5 rounded-full bg-gradient-to-r from-[#DA2128] to-[#DB6400] px-6 py-2.5 text-[12.5px] font-bold text-white shadow-lg shadow-[#DA2128]/25 transition-all hover:shadow-[#DA2128]/40"
                    >
                      Clear search
                    </button>
                  )}
                </div>
              )}

              {/* Pagination */}
              {pressTotalPages > 1 && (
                <div className="mt-10 flex items-center justify-center gap-2">
                  <button
                    disabled={pressClampedPage === 1}
                    onClick={() => changePressPage(pressClampedPage - 1)}
                    className="grid h-10 w-10 place-items-center rounded-xl text-stone-500 ring-1 ring-stone-200 transition-all hover:text-[#DA2128] hover:ring-[#DA2128] disabled:pointer-events-none disabled:opacity-35"
                    aria-label="Previous page"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  {Array.from({ length: pressTotalPages }, (_, i) => i + 1).map((n) => (
                    <button
                      key={n}
                      onClick={() => changePressPage(n)}
                      className={`relative h-10 w-10 rounded-xl text-[13px] font-extrabold transition-all ${
                        n === pressClampedPage
                          ? "text-white"
                          : "text-stone-500 ring-1 ring-stone-200 hover:text-[#DA2128] hover:ring-[#DA2128]"
                      }`}
                    >
                      {n === pressClampedPage && (
                        <motion.span
                          layoutId="press-page-pill"
                          className="absolute inset-0 rounded-xl bg-gradient-to-br from-[#DA2128] to-[#DB6400] shadow-lg shadow-[#DA2128]/30"
                          transition={{ type: "spring", stiffness: 420, damping: 32 }}
                        />
                      )}
                      <span className="relative">{n}</span>
                    </button>
                  ))}
                  <button
                    disabled={pressClampedPage === pressTotalPages}
                    onClick={() => changePressPage(pressClampedPage + 1)}
                    className="grid h-10 w-10 place-items-center rounded-xl text-stone-500 ring-1 ring-stone-200 transition-all hover:text-[#DA2128] hover:ring-[#DA2128] disabled:pointer-events-none disabled:opacity-35"
                    aria-label="Next page"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* =============================== EVENTS =============================== */}
        <section
          id="events"
          className="sector-section relative scroll-mt-6 overflow-hidden bg-white py-20 lg:py-24"
        >
          <div className="pointer-events-none absolute -right-24 top-10 h-72 w-72 rounded-full bg-[#F59C00]/10 blur-3xl" />
          <div className="pointer-events-none absolute -left-24 bottom-0 h-72 w-72 rounded-full bg-[#DA2128]/10 blur-3xl" />

          <div className="relative mx-auto max-w-7xl px-6">
            <SectionHeading
              eyebrow="Engagements & Events"
              title="Recent & Upcoming Initiatives"
            />

            <div className="mt-14 grid gap-6 lg:grid-cols-[300px_minmax(0,1fr)] xl:grid-cols-[320px_minmax(0,1fr)]">
              {/* Sidebar */}
              <Reveal variant="left">
                <aside className="rounded-[26px] border border-[#DA2128]/10 bg-[#FAF7F2] p-5 shadow-[0_18px_44px_rgba(218,33,40,0.08)] lg:sticky lg:top-24 lg:h-fit">
                  <div className="mb-5 flex items-center justify-between border-b border-[#DA2128]/10 pb-3.5">
                    <div>
                      <p className="text-[0.7rem] font-semibold uppercase tracking-[0.22em] text-[#DB6400]">
                        Filter by
                      </p>
                      <h3 style={SERIF} className="mt-1 text-xl font-bold text-stone-900">
                        Events Search
                      </h3>
                    </div>
                    <div className="rounded-xl bg-gradient-to-br from-[#FFE3DA] to-[#FFE7CF] p-2 text-[#DA2128]">
                      <SlidersHorizontal className="h-4 w-4" />
                    </div>
                  </div>

                  <div className="space-y-3.5">
                    <div className="space-y-1.5">
                      <label className="text-[0.8rem] font-bold text-stone-700">Keywords</label>
                      <div className="relative">
                        <Search
                          className="pointer-events-none absolute left-3.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[#DA2128]"
                          strokeWidth={2.2}
                        />
                        <input
                          type="text"
                          value={eventKeyword}
                          onChange={(e) => setEventKeyword(e.target.value)}
                          placeholder="Search by title, place..."
                          className="w-full rounded-xl border border-[#DA2128]/10 bg-white py-2.5 pl-10 pr-3.5 text-[0.8rem] font-medium text-stone-700 transition duration-300 placeholder:text-stone-400 hover:border-[#DA2128]/30 focus:border-[#DA2128] focus:outline-none focus:ring-2 focus:ring-[#DA2128]/10"
                        />
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-[0.8rem] font-bold text-stone-700">Status</label>
                      <div className="flex gap-1.5 rounded-xl border border-[#DA2128]/10 bg-white p-1">
                        {[
                          ["all", "All"],
                          ["forthcoming", "Forthcoming"],
                          ["past", "Past"],
                        ].map(([v, l]) => (
                          <button
                            key={v}
                            type="button"
                            onClick={() => setEventTab(v)}
                            className={`flex-1 rounded-lg px-2 py-2 text-[0.75rem] font-bold transition duration-300 ${
                              eventTab === v
                                ? "bg-gradient-to-r from-[#DA2128] to-[#F59C00] text-white shadow-md"
                                : "text-stone-500 hover:bg-[#FAF7F2]"
                            }`}
                          >
                            {l}
                          </button>
                        ))}
                      </div>
                    </div>

                    {eventTags.length > 0 && (
                      <div className="space-y-1.5">
                        <label className="text-[0.8rem] font-bold text-stone-700">Category</label>
                        <div className="relative">
                          <Tag
                            className="pointer-events-none absolute left-3.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[#DA2128]"
                            strokeWidth={2.2}
                          />
                          <select
                            value={eventTag}
                            onChange={(e) => setEventTag(e.target.value)}
                            className="w-full appearance-none rounded-xl border border-[#DA2128]/10 bg-white py-2.5 pl-10 pr-9 text-[0.8rem] font-medium text-stone-700 transition duration-300 hover:border-[#DA2128]/30 focus:border-[#DA2128] focus:outline-none focus:ring-2 focus:ring-[#DA2128]/10"
                          >
                            <option value="">All categories</option>
                            {eventTags.map((t) => (
                              <option key={t} value={t}>
                                {t}
                              </option>
                            ))}
                          </select>
                          <svg
                            viewBox="0 0 24 24"
                            className="pointer-events-none absolute right-3.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-stone-400"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="m6 9 6 6 6-6" />
                          </svg>
                        </div>
                      </div>
                    )}

                    {eventPlaces.length > 0 && (
                      <div className="space-y-1.5">
                        <label className="text-[0.8rem] font-bold text-stone-700">Location</label>
                        <div className="relative">
                          <MapPin
                            className="pointer-events-none absolute left-3.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[#DA2128]"
                            strokeWidth={2.2}
                          />
                          <select
                            value={eventPlace}
                            onChange={(e) => setEventPlace(e.target.value)}
                            className="w-full appearance-none rounded-xl border border-[#DA2128]/10 bg-white py-2.5 pl-10 pr-9 text-[0.8rem] font-medium text-stone-700 transition duration-300 hover:border-[#DA2128]/30 focus:border-[#DA2128] focus:outline-none focus:ring-2 focus:ring-[#DA2128]/10"
                          >
                            <option value="">All locations</option>
                            {eventPlaces.map((p) => (
                              <option key={p} value={p}>
                                {p}
                              </option>
                            ))}
                          </select>
                          <svg
                            viewBox="0 0 24 24"
                            className="pointer-events-none absolute right-3.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-stone-400"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="m6 9 6 6 6-6" />
                          </svg>
                        </div>
                      </div>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={clearEventFilters}
                    className="mt-5 inline-flex w-full items-center justify-center gap-1.5 rounded-xl border border-[#DA2128]/20 bg-white px-4 py-2.5 text-[0.8rem] font-bold text-[#DA2128] transition duration-300 hover:bg-[#FEF7F4]"
                  >
                    <X className="h-3.5 w-3.5" strokeWidth={2.4} />
                    Clear filters
                  </button>
                </aside>
              </Reveal>

              {/* Main */}
              <div>
                <div className="flex flex-wrap items-center justify-between gap-3 pb-5">
                  <span className="text-[12.5px] font-semibold text-stone-500">
                    {filteredEvents.length === 0
                      ? EVENTS.length === 0
                        ? "No events published yet for this council"
                        : "No events match your filters"
                      : `Showing ${Math.min(eventsVisible, filteredEvents.length)} of ${
                          filteredEvents.length
                        } event${filteredEvents.length > 1 ? "s" : ""}`}
                  </span>
                </div>

                <div key={eventAnimKey} className="grid gap-6">
                  {filteredEvents.length === 0 ? (
                    <div className="rounded-[24px] border border-dashed border-[#DA2128]/25 bg-[#FAF7F2] p-10 text-center">
                      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-[#DA2128] to-[#F59C00] text-white shadow-lg shadow-[#DA2128]/25">
                        <Search className="h-6 w-6" />
                      </div>
                      <h4 style={SERIF} className="mt-4 text-lg font-bold text-stone-900">
                        {EVENTS.length === 0
                          ? "No engagements published yet"
                          : "No matching events found"}
                      </h4>
                      <p className="mx-auto mt-2 max-w-sm text-[13px] leading-relaxed text-stone-500">
                        {EVENTS.length === 0
                          ? "Check back soon for this council's upcoming and past engagements."
                          : "Try clearing a filter or switching the status tab to see more initiatives."}
                      </p>
                      {EVENTS.length > 0 && (
                        <button
                          type="button"
                          onClick={clearEventFilters}
                          className="mt-5 inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-[#DA2128] to-[#F59C00] px-5 py-2.5 text-[12.5px] font-bold text-white shadow-lg shadow-[#DA2128]/25 transition hover:shadow-[#DA2128]/40"
                        >
                          Reset all filters
                        </button>
                      )}
                    </div>
                  ) : (
                    visibleEvents.map((event, i) => (
                      <SectorEventCard
                        key={`${event.title}-${eventAnimKey}-${i}`}
                        event={event}
                        index={i}
                      />
                    ))
                  )}
                </div>

                {/* Load More */}
                {hasMoreEvents && (
                  <div className="mt-8 text-center">
                    <button
                      type="button"
                      onClick={loadMoreEvents}
                      className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#DA2128] to-[#F59C00] px-8 py-3 text-[13px] font-bold text-white shadow-lg shadow-[#DA2128]/30 transition-all hover:-translate-y-0.5 hover:shadow-[#DA2128]/45"
                    >
                      Load More Events
                      <Icon name="chevron" className="h-4 w-4 rotate-90" strokeWidth={2.2} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* =============================== SECRETARIAT CONTACT =============================== */}
        <section
          id="secretariat"
          className="sector-section relative scroll-mt-6 overflow-hidden bg-gradient-to-b from-white to-[#FAF7F2] py-20 lg:py-24"
        >
          <div className="relative mx-auto max-w-7xl px-6">
            <div className="grid gap-10 lg:grid-cols-2">
              {/* Left Column - Contact Information */}
              <div>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="mb-3 inline-flex items-center gap-2"
                >
                  <div className="h-[2px] w-6 rounded-full bg-[#DA2128]" />
                  <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#DA2128]">
                    Contact
                  </span>
                </motion.div>

                <motion.h2
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 }}
                  style={SERIF}
                  className="mb-3 text-3xl font-bold leading-[1.1] tracking-tight sm:text-4xl text-stone-900"
                >
                  Contact <span className="text-[#DA2128]">Us</span>
                </motion.h2>

                <div className="space-y-6">
  {/* HOD Name */}
  <motion.div
    initial={{ opacity: 0, x: -15 }}
    whileInView={{ opacity: 1, x: 0 }}
    transition={{ delay: 0.3 }}
    viewport={{ once: true }}
    className="flex items-start gap-4 rounded-xl transition-colors hover:bg-red-50/30"
  >
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#DA2128]/10 text-[#DA2128]">
      <span className="text-sm font-black">H</span>
    </div>

    <div>
      <h3 className="mb-1 text-sm font-bold text-stone-900">Council Head</h3>
      <p className="text-sm font-semibold text-[#DA2128]">
        {sectorContact.hodName}
      </p>
      {/* <p className="mt-0.5 text-xs text-stone-500">
        ASSOCHAM Council Secretariat
      </p> */}
    </div>
  </motion.div>

  {/* Address */}
  <motion.div
    initial={{ opacity: 0, x: -15 }}
    whileInView={{ opacity: 1, x: 0 }}
    transition={{ delay: 0.4 }}
    viewport={{ once: true }}
    className="flex items-start gap-4 rounded-xl transition-colors hover:bg-red-50/30"
  >
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#DA2128]/10 text-[#DA2128]">
      <MapPin className="h-5 w-5" strokeWidth={2} />
    </div>

    <div>
      <h3 className="mb-1 text-sm font-bold text-stone-900">Address</h3>

      <a
        href={sectorContact.addressUrl}
        target="_blank"
        rel="noreferrer"
        className="text-sm leading-relaxed text-stone-600 transition-colors hover:text-[#DA2128]"
      >
        {sectorContact.address.map((line) => (
          <span key={line} className="block">
            {line}
          </span>
        ))}
      </a>
    </div>
  </motion.div>

  {/* HOD Email */}
  <motion.div
    initial={{ opacity: 0, x: -15 }}
    whileInView={{ opacity: 1, x: 0 }}
    transition={{ delay: 0.5 }}
    viewport={{ once: true }}
    className="flex items-start gap-4 rounded-xl transition-colors hover:bg-red-50/30"
  >
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#DA2128]/10 text-[#DA2128]">
      <Mail className="h-5 w-5" strokeWidth={2} />
    </div>

    <div>
      <h3 className="mb-1 text-sm font-bold text-stone-900">Email</h3>

      <a
        href={`mailto:${sectorContact.email}`}
        className="text-sm font-semibold text-[#DA2128] transition-colors hover:text-[#DB6400]"
      >
        {sectorContact.email}
      </a>

      <p className="mt-0.5 text-xs text-stone-500">
        For queries related to {sector.title}.
      </p>
    </div>
  </motion.div>

  {/* Phone Number */}
  <motion.div
    initial={{ opacity: 0, x: -15 }}
    whileInView={{ opacity: 1, x: 0 }}
    transition={{ delay: 0.6 }}
    viewport={{ once: true }}
    className="flex items-start gap-4 rounded-xl transition-colors hover:bg-red-50/30"
  >
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#DA2128]/10 text-[#DA2128]">
      <Phone className="h-5 w-5" strokeWidth={2} />
    </div>

    <div>
      <h3 className="mb-1 text-sm font-bold text-stone-900">Phone Number</h3>

      <a
        href={`tel:${sectorContact.phone.replace(/\D/g, "")}`}
        className="text-sm font-semibold text-[#DA2128] transition-colors hover:text-[#DB6400]"
      >
        {sectorContact.phone}
      </a>

      <p className="mt-0.5 text-xs text-stone-500">
        Mon–Fri, 9:30 AM – 6:00 PM IST
      </p>
    </div>
  </motion.div>
</div>
              </div>

              {/* Right Column - Contact Form */}
              {/* <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="rounded-2xl bg-white p-6 shadow-xl border border-stone-200"
              >
                <AnimatePresence mode="wait">
                  {submitted ? (
                    <motion.div
                      key="success"
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      className="flex flex-col items-center justify-center py-10 text-center"
                    >
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", delay: 0.2 }}
                        className="mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-green-100"
                      >
                        <CheckCircle2 className="h-8 w-8 text-green-500" strokeWidth={2.2} />
                      </motion.div>
                      <h3 style={SERIF} className="mb-1 text-xl font-bold text-stone-900">
                        Query Submitted!
                      </h3>
                      <p className="text-sm text-gray-500">Our team will respond within 48 hours.</p>
                    </motion.div>
                  ) : (
                    <motion.form
                      key="form"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      onSubmit={handleSubmit}
                      className="space-y-3"
                    >
                      <h3 style={SERIF} className="text-xl font-bold text-stone-900">
                        Send us a <span className="text-[#DA2128]">Message</span>
                      </h3>

                      <div className="grid gap-3 sm:grid-cols-2">
                        <input
                          type="text"
                          placeholder="Full Name"
                          required
                          className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-xs transition-all focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-100"
                        />
                        <input
                          type="email"
                          placeholder="Email"
                          required
                          className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-xs transition-all focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-100"
                        />
                      </div>

                      <input
                        type="text"
                        placeholder="Organization"
                        className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-xs transition-all focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-100"
                      />

                      <select
                        required
                        defaultValue=""
                        className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-xs transition-all focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-100"
                      >
                        <option value="">Select a category</option>
                        <option>Policy & Schemes</option>
                        <option>Investment Opportunities</option>
                        <option>Regulatory Compliance</option>
                        <option>Technology & Innovation</option>
                        <option>Market Access</option>
                        <option>Other</option>
                      </select>

                      <textarea
                        rows={4}
                        placeholder="Describe your query..."
                        required
                        className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-xs transition-all focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-100"
                      />

                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        type="submit"
                        className="flex w-full items-center justify-center gap-2 rounded-lg py-3 text-xs font-bold text-white shadow-lg"
                        style={{ background: `linear-gradient(135deg, #DA2128, #F59C00)` }}
                      >
                        <Send className="h-3.5 w-3.5" strokeWidth={2.2} />
                        Submit Query
                      </motion.button>
                    </motion.form>
                  )}
                </AnimatePresence>
              </motion.div> */}

              <QueryForm sector={sector} />
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

/* ========================== EVENT CALENDAR BADGE ========================== */

function SectorCalendarBadge({ date, active }) {
  const { dayPart, monthPart, year } = getDateParts(date);

  return (
    <div className="relative mx-auto flex w-[88px] shrink-0 flex-col overflow-hidden rounded-[18px] border border-white/80 bg-white shadow-[0_10px_28px_rgba(219,38,40,0.12)] transition-all duration-500 group-hover:-translate-y-1.5 group-hover:rotate-[-1deg] group-hover:shadow-[0_18px_40px_rgba(219,38,40,0.18)] sm:mx-0">
      <div className="absolute left-3 top-0 h-2.5 w-2.5 -translate-y-1/2 rounded-full border border-[#DA2128]/30 bg-[#FEF7F4] transition duration-500 group-hover:bg-[#F59C00]" />
      <div className="absolute right-3 top-0 h-2.5 w-2.5 -translate-y-1/2 rounded-full border border-[#DA2128]/30 bg-[#FEF7F4] transition duration-500 group-hover:bg-[#F59C00]" />
      <div className="event-card-shine pointer-events-none absolute inset-0" />

      <div className="relative bg-gradient-to-br from-white to-[#FAF7F2] px-2.5 py-3 text-center">
        <div className="font-heading text-[1.55rem] font-black leading-none text-stone-900">{dayPart}</div>
        <div className="mt-1.5 text-[0.65rem] font-extrabold uppercase tracking-[0.22em] text-[#DB6400]">
          {monthPart}
        </div>
      </div>

      <div
        className={`relative px-2.5 py-2 text-center text-[0.7rem] font-extrabold tracking-[0.18em] text-white ${
          active
            ? "bg-gradient-to-r from-[#DA2128] to-[#F59C00]"
            : "bg-gradient-to-r from-stone-500 to-stone-600"
        }`}
      >
        <span className="relative z-10">{year}</span>
      </div>
    </div>
  );
}

/* ========================== EVENT CARD ========================== */

function SectorEventCard({ event, index }) {
  const active = isEventUpcoming(event.date);
  const delay = `${Math.min(index * 70, 600)}ms`;

  return (
    <article
      style={{ animationDelay: delay }}
      className="event-fade-up group relative overflow-hidden rounded-[24px] border border-[#DA2128]/10 bg-white p-4 shadow-[0_12px_32px_rgba(218,33,40,0.07)] transition-all duration-500 hover:-translate-y-1.5 hover:border-[#DA2128]/25 hover:shadow-[0_20px_48px_rgba(218,33,40,0.14)] sm:p-5"
    >
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(245,156,0,0.12),transparent_32%),radial-gradient(circle_at_bottom_left,rgba(218,33,40,0.1),transparent_35%)] opacity-80 transition-opacity duration-500 group-hover:opacity-100" />
      <div className="pointer-events-none absolute -left-1/2 top-0 h-full w-1/2 -rotate-12 bg-[linear-gradient(115deg,transparent,rgba(255,255,255,0.8),transparent)] opacity-0 transition-all duration-700 group-hover:left-[120%] group-hover:opacity-100" />
      <div className="event-glow-border pointer-events-none absolute inset-0 rounded-[24px] opacity-0 transition-opacity duration-500 group-hover:opacity-100" />

      <div className="relative flex flex-col gap-4 sm:flex-row sm:items-start">
        <SectorCalendarBadge date={event.date} active={active} />

        <div className="min-w-0 flex-1 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex rounded-full bg-[#FAF7F2] px-2.5 py-1 text-[0.65rem] font-extrabold uppercase tracking-[0.18em] text-[#DA2128] transition duration-300 group-hover:scale-105">
              {event.tag || "Event"}
            </span>
            {event.place && (
              <span className="inline-flex rounded-full border border-[#DB6400]/25 bg-[#FEF7F4] px-2.5 py-1 text-[0.7rem] font-bold text-[#DB6400] transition duration-300 group-hover:-translate-y-0.5">
                {event.place}
              </span>
            )}
            {active ? (
              <span className="event-live-pill inline-flex items-center gap-1.5 rounded-full bg-[#FFF0EC] px-2.5 py-1 text-[0.65rem] font-extrabold uppercase tracking-[0.18em] text-[#DA2128]">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#DA2128] opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-[#DA2128]" />
                </span>
                Upcoming
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 bg-stone-50 px-2.5 py-1 text-[0.65rem] font-extrabold uppercase tracking-[0.18em] text-stone-500">
                Archived
              </span>
            )}
          </div>

          <h3 className="font-heading text-[1.05rem] font-extrabold leading-snug text-stone-900 transition-all duration-500 group-hover:translate-x-0.5 group-hover:text-[#DA2128] sm:text-[1.15rem]">
            {event.title}
          </h3>

          <p className="text-[13.5px] leading-relaxed text-stone-600">{event.text}</p>

          {event.highlight && (
            <div className="flex items-start gap-3 rounded-2xl bg-[#FAF7F2] p-3.5">
              <Icon name="check" className="mt-0.5 h-4 w-4 shrink-0 text-[#DA2128]" strokeWidth={2.8} />
              <p className="text-[13px] font-medium leading-relaxed text-stone-700">{event.highlight}</p>
            </div>
          )}

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2.5 rounded-full bg-[#FFFDFC] px-3 py-1.5 text-[0.8rem] font-semibold text-stone-600 shadow-sm transition duration-300 group-hover:shadow-md">
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-[#DA2128] to-[#F59C00] text-white shadow-[0_6px_14px_rgba(218,33,40,0.22)]">
                <CalendarDays className="h-3.5 w-3.5" strokeWidth={2.2} />
              </span>
              <span>{event.date}</span>
            </div>

            <button
              type="button"
              className="inline-flex w-fit items-center gap-1.5 rounded-full bg-gradient-to-r from-[#DA2128] to-[#F59C00] px-4 py-2 text-[0.8rem] font-bold text-white shadow-[0_10px_22px_rgba(218,33,40,0.22)] transition-all duration-300 hover:scale-[1.03] hover:shadow-[0_14px_28px_rgba(218,33,40,0.3)] active:scale-[0.98]"
            >
              <span>{active ? "Register Interest" : "View Highlights"}</span>
              <svg
                viewBox="0 0 24 24"
                className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
              >
                <path d="M5 12h14" />
                <path d="m13 5 7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </article>
  );
}
